import { Alert, SelectChangeEvent } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useAppSelector } from "app/hooks";
import HolzfinderDateInputWithoutTimeLabled from "components/inputs/HolzfinderDateInputWithoutTimeLabled";
import HolzfinderInputLabled from "components/inputs/HolzfinderInputLabled";
import HolzfinderSelect from "components/inputs/HolzfinderSelect";
import { selectCertificateTypes } from "features/certificateTypes/certificateTypesSlice";
import { trimAndCheck } from "functions/trimAndCheck";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

interface CertifcateFormProps { 
  certificate: string; 
  certificateNumber: string;
  logoRegNumber: string;
  validFrom: string;
  validTo: string;
  handleCertificateChange: (value: string) => void;
  handleCertificateNumberChange: (value: string) => void;
  handleLogoRegNumberChange: (value: string) => void;
  handleValidFromChange: (value: string) => void;
  handleValidToChange: (value: string) => void;
  onValidationChange: (isValid: boolean, error?: string | null) => void; 
}

export default function CertificateForm(props: CertifcateFormProps) { 
  const {
    certificate,
    certificateNumber,
    logoRegNumber,
    validFrom,
    validTo,
    handleCertificateChange,
    handleCertificateNumberChange,
    handleLogoRegNumberChange,
    handleValidFromChange,
    handleValidToChange,
    onValidationChange // Neue Prop destrukturieren
  } = props;
  const certificateTypes = useAppSelector(selectCertificateTypes);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [validFromInputState, setValidFromInputState] = useState<boolean>(true);
  const [validToInputState, setValidToInputState] = useState<boolean>(true);

  useEffect(() => {
   
    setFormErrors({}); // Reset form errors when dates are empty
    if (!validFrom || !validTo) {
      console.log("One or both dates are empty, resetting errors.");
      setError(null); // Or set a specific message if empty dates are not allowed initially
      onValidationChange(true); // Assuming empty dates are initially valid or not yet an error
      return;
    }

    const validToDate = validTo ? DateTime.fromISO(validTo) : null;
    const validFromDate = validFrom ? DateTime.fromISO(validFrom) : null;

    let currentError: string | null = null;
    if ((validFrom && !validFromDate?.isValid) || !validFromInputState) {
      currentError = "Ungültiges Startdatum. Bitte Format tt-mm-jjjj verwenden.";
      setFormErrors((prev) => ({
        ...prev,
        validFrom: ["Ungültiges Startdatum. Bitte Format tt-mm-jjjj verwenden."],
      }));
    } else if ((validTo && !validToDate?.isValid) || !validToInputState) {
      currentError = "Ungültiges Enddatum. Bitte Format tt-mm-jjjj verwenden.";
      setFormErrors((prev) => ({
        ...prev,
        validTo: ["Ungültiges Enddatum. Bitte Format tt-mm-jjjj verwenden."],
      }));
    } else if (validFromDate && validToDate && validFromDate.isValid && validToDate.isValid) {
      if (validFromDate >= validToDate) { // Logik korrigiert: Startdatum muss VOR Enddatum liegen
        currentError = "Das Startdatum muss vor dem Enddatum liegen.";
        setFormErrors((prev) => ({
          ...prev,
          validFrom: ["Das Startdatum muss vor dem Enddatum liegen."],
          validTo: ["Enddatum muss nach Startdatum liegen."],
        }));
      }
    }

    setError(currentError);
    onValidationChange(currentError === null, currentError); // Callback aufrufen
  }, [validFrom, validTo, onValidationChange, validFromInputState, validToInputState]);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
        <Grid xs={12} md={3}>
          <HolzfinderSelect
            sx={{ width: "100%" }}
            id="tax_type"
            label="Zertifikat"
            options={certificateTypes.map((type) => ({ value: type.id, label: type.short_name }))}
            currentValue={certificate}
            onChange={(event: SelectChangeEvent<unknown>) => handleCertificateChange((event.target as HTMLSelectElement).value)}
          />
        </Grid>
        <Grid xs={12} md={3}>
          <HolzfinderInputLabled
            sx={{ width: "100%" }}
            id={"certificate_number"}
            label={"Zertifikat-Nr."}
            placeholder="Zertifikat-Nr."
            value={certificateNumber}
            inputProps={{ maxLength: 25 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              trimAndCheck(event, {
                maxLength: 25,
                onChange: handleCertificateNumberChange,
              });

            }
            }
          />
        </Grid>
        <Grid xs={12} md={3}>
          <HolzfinderInputLabled
            id={"logo_reg_number"}
            label={"LogoRegNr."}
            placeholder="LogoRegNr."
            value={logoRegNumber}
            inputProps={{ maxLength: 50 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              trimAndCheck(event, {
                maxLength: 50,
                onChange: handleLogoRegNumberChange,
              });
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
        <Grid xs={12} md={3}>
          <HolzfinderDateInputWithoutTimeLabled
            label="Gültig von"
            value={validFrom}
            onChange={handleValidFromChange}
            onValid={(valid) => setValidFromInputState(valid)}
            error={!!formErrors.validFrom}
          />
        </Grid>
        <Grid xs={12} md={3}>
          <HolzfinderDateInputWithoutTimeLabled
            label="Gültig bis"
            value={validTo}
            onChange={handleValidToChange}
            onValid={(valid) => setValidToInputState(valid)}
            error={!!formErrors.validTo}
          />
        </Grid>
        <Grid xs={12}>
          {error && <Alert severity="error">{error}</Alert>}
        </Grid>
      </Grid>
    </>
  )
}